import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import type { BrazeBanner } from '~/lib/braze.client';
import {
  brazeInit,
  removeBrazeSubscription,
  requestBannersRefresh,
  subscribeToBannersUpdates,
} from '~/lib/braze.client';
import { useRootLoaderData } from './useRootLoaderData';

export type UseBrazeBannersResult = {
  /** The resolved banners for `placementIds` that currently have content, in order. */
  banners: BrazeBanner[];
  /** Whether the banners refresh request for `placementIds` is still in flight. */
  isLoading: boolean;
};

/**
 * The Braze Web SDK rate-limits requests to a given endpoint locally and, when
 * a request arrives while a retry for that same endpoint is already scheduled,
 * silently drops the new request without ever invoking either callback -
 * requestBannersRefresh's onSettled can then never fire. This safety net caps
 * how long we'll wait for that scenario before treating the round as failed -
 * both for the per-hook loading state, and (via `dispatchCoordinatedRefresh`'s
 * watchdog) for the shared coordinator lock below, which would otherwise stay
 * held forever and block every future request in the app, not just this one.
 */
const SETTLE_TIMEOUT_MS = 10_000;

type SettleCallback = () => void;

/**
 * Every `/banners/sync` response REPLACES the SDK's entire local banner cache
 * with only the placement IDs that request asked for, and broadcasts that
 * narrowed cache to every `subscribeToBannersUpdates` subscriber in the app -
 * not just the caller that triggered it. Two independent `useBrazeBanners`
 * callers (e.g. the products page carousel and the cart drawer's side banner)
 * therefore fight each other: whichever settles last wipes the other's
 * banners from every other subscriber's state, and since both share the same
 * SDK-side rate-limited endpoint, one of the two requests can also be
 * silently dropped entirely (no network call, no callback) if it arrives
 * while the other is still in flight.
 *
 * This module-level coordinator makes every `useBrazeBanners` instance in the
 * app share one `requestBannersRefresh` call for the union of everyone's
 * currently-needed placement IDs, so a request is never made for - or
 * received on - a subset of them. A registration that arrives while a
 * request is already in flight is folded into a follow-up request once that
 * one settles, rather than firing a second, competing request immediately.
 */
let registrations = new Map<number, readonly string[]>();
let nextRegistrationId = 0;
let inFlightUnion: string[] | null = null;
let inFlightSettleCallbacks: SettleCallback[] = [];
let queuedSettleCallbacks: SettleCallback[] = [];
let dispatchScheduled = false;
/**
 * Set when `invalidateBrazeBannersCache` is called while a round is already in flight, since that
 * round's response is for whatever user/state existed *before* the invalidation and can't be
 * trusted as the new authoritative answer. Read and cleared by that round's own `finishRound`.
 */
let invalidatedDuringInFlightRound = false;

/** Notified with the set of placement IDs dropped from `bannerCache` whenever a round prunes it, keyed by registration ID - so an already-mounted hook can clear them from its own state instead of rendering a banner the SDK's store no longer holds. */
let pruneListeners = new Map<number, (prunedPlacementIds: ReadonlySet<string>) => void>();

/**
 * Backoff before retrying a round that never actually reached the network.
 * The SDK's local rate limiter (see `requestBannersRefresh` in
 * `braze.client.ts`) calls its error callback synchronously and immediately
 * when it declines to send a request at all, so retrying with no delay would
 * just trip the same limiter again. Doubles after each consecutive
 * rate-limited/errored round, resets after any round that actually completes
 * a request, and is capped at `SETTLE_TIMEOUT_MS` so a still-registered
 * caller is never left waiting longer than the existing watchdog already
 * allows for.
 */
const RETRY_BASE_DELAY_MS = 1_000;
let retryDelayMs = RETRY_BASE_DELAY_MS;
let retryTimeoutId: number | undefined;

/**
 * The last banner (or `null`, meaning "resolved, no banner") seen for each
 * placement ID, shared across every `useBrazeBanners` instance and kept for
 * the lifetime of the page - not just the mounted lifetime of whichever
 * component originally fetched it. A component that requests a placement ID
 * some *other* component (or an earlier mount of itself, e.g. after
 * navigating away and back) already resolved starts from that known-good
 * value immediately instead of blanking to "no banner" until its own
 * request completes - which matters because the SDK can silently drop a
 * `requestBannersRefresh` call outright (see `SETTLE_TIMEOUT_MS` above) if
 * it arrives too soon after another one, something a fresh remount has no
 * way to avoid triggering.
 */
let bannerCache: Record<string, BrazeBanner | null> = {};

/**
 * Placement IDs the SDK's store is currently known to have an authoritative answer for -
 * whether that's a real banner or "no banner assigned" - as of the last successful round.
 * Unlike `bannerCache` (which the SDK never writes a key into for a placement with no banner
 * assigned, since broadcasts only carry keys for placements that actually resolved to
 * something), this is populated from the *request* union itself, so it can represent "fetched,
 * nothing assigned" as well as "fetched, got a banner". Replaced wholesale (not merged) on every
 * successful round, mirroring how that round's `/banners/sync` replaces the SDK's entire store.
 */
let knownPlacementIds = new Set<string>();

/**
 * The subset of `bannerCache` already known for `placementIds`, keyed the same way a broadcast update is.
 * @param placementIds
 */
function getCachedBanners(placementIds: readonly string[]): Record<string, BrazeBanner | null> {
  const cached: Record<string, BrazeBanner | null> = {};
  for (const placementId of placementIds) {
    if (placementId in bannerCache) {
      cached[placementId] = bannerCache[placementId];
    }
  }
  return cached;
}

/** All placement IDs currently registered by any mounted `useBrazeBanners` instance, deduplicated. */
function getRegisteredPlacementIdsUnion(): string[] {
  const union = new Set<string>();
  for (const placementIds of registrations.values()) {
    for (const placementId of placementIds) {
      union.add(placementId);
    }
  }
  return Array.from(union);
}

/**
 * Retries a round that never actually reached the network, once the backoff
 * from `retryDelayMs` elapses - only if something is still registered and no
 * other round is already in flight or about to be (a normal registration
 * arriving in the meantime already covers it).
 */
function scheduleRetry() {
  window.clearTimeout(retryTimeoutId);
  const delay = retryDelayMs;
  retryDelayMs = Math.min(retryDelayMs * 2, SETTLE_TIMEOUT_MS);
  retryTimeoutId = window.setTimeout(() => {
    retryTimeoutId = undefined;
    if (inFlightUnion === null && !dispatchScheduled && getRegisteredPlacementIdsUnion().length > 0) {
      dispatchCoordinatedRefresh();
    }
  }, delay);
}

/**
 * Issues one `requestBannersRefresh` for the current union, settling every
 * queued callback once it resolves.
 *
 * The SDK's own local rate limiter can decline to send this request at all -
 * calling its error callback synchronously, with no network request ever
 * made (see `requestBannersRefresh` in `braze.client.ts`) - and it can also
 * silently drop the call outright, with no callback ever firing (see
 * `SETTLE_TIMEOUT_MS` above; without the watchdog below that would leave
 * `inFlightUnion` set forever, since `requestCoordinatedBannersRefresh` only
 * ever dispatches a new request when nothing is already in flight). Neither
 * case means the union it asked for is actually up to date, so both retry
 * via `scheduleRetry` instead of being treated as a resolved round -
 * `roundSettled` ensures only the first of the real callback or the watchdog
 * actually runs the settle logic.
 */
function dispatchCoordinatedRefresh() {
  const union = getRegisteredPlacementIdsUnion();
  const callbacksForThisRound = queuedSettleCallbacks;
  queuedSettleCallbacks = [];

  if (union.length === 0) {
    callbacksForThisRound.forEach((settle) => settle());
    return;
  }

  // Every currently-needed placement already has a known value (from this or
  // an earlier round) - there's nothing to recover from, so there's no
  // reason to spend a live request (and the SDK's local rate-limit budget it
  // consumes) re-fetching data we already have. This is what actually lets
  // that budget recover between navigations instead of every page visit
  // re-tripping it and restarting the backoff cascade for everyone.
  if (union.every((placementId) => knownPlacementIds.has(placementId))) {
    callbacksForThisRound.forEach((settle) => settle());
    return;
  }

  inFlightUnion = union;
  inFlightSettleCallbacks = callbacksForThisRound;

  let roundSettled = false;
  const finishRound = (succeeded: boolean) => {
    if (roundSettled) {
      return;
    }
    roundSettled = true;
    window.clearTimeout(watchdogId);

    const settledCallbacks = inFlightSettleCallbacks;
    const fetchedUnion = inFlightUnion;
    inFlightUnion = null;
    inFlightSettleCallbacks = [];
    settledCallbacks.forEach((settle) => settle());

    const wasInvalidatedDuringRound = invalidatedDuringInFlightRound;
    invalidatedDuringInFlightRound = false;

    if (!succeeded) {
      // Nothing was actually fetched, so whatever's still registered is
      // exactly as stale as before this round started - retry it rather
      // than treating the round as resolved.
      if (getRegisteredPlacementIdsUnion().length > 0 || queuedSettleCallbacks.length > 0) {
        scheduleRetry();
      }
      return;
    }

    if (wasInvalidatedDuringRound) {
      // This round's response is for whatever the SDK's store held *before*
      // `invalidateBrazeBannersCache` ran mid-flight, so it must not be treated as the new
      // authoritative `knownPlacementIds`/`bannerCache` - those were already reset to empty by
      // the invalidation itself, and there's nothing from this stale response to prune. Force a
      // fresh round for whatever's still registered instead of silently settling as if this one
      // covered it, which would otherwise leave those placements marked "known" (with no cached
      // banner) and never re-fetched again.
      if (getRegisteredPlacementIdsUnion().length > 0 || queuedSettleCallbacks.length > 0) {
        dispatchCoordinatedRefresh();
      }
      return;
    }

    retryDelayMs = RETRY_BASE_DELAY_MS;

    // This round's request just REPLACED the SDK's entire internal banner
    // store with only `fetchedUnion` (see the module comment above) - any
    // other placement ID `bannerCache` still remembers from an earlier, now
    // superseded round is no longer retrievable from the SDK at all, even
    // though the value cached for it here is technically still accurate.
    // Left in place, `dispatchCoordinatedRefresh`'s "already cached" check
    // would keep trusting it forever and never re-request it, leaving that
    // banner's own container (which reads straight from the SDK's store via
    // `braze.getBanner`, not from this cache) permanently empty. Drop those
    // entries so the next registration that needs one of them sees it's
    // missing and issues a real request to re-populate the SDK's store.
    const fetchedPlacementIds = new Set(fetchedUnion);
    knownPlacementIds = fetchedPlacementIds;
    const prunedPlacementIds = new Set<string>();
    for (const placementId of Object.keys(bannerCache)) {
      if (!fetchedPlacementIds.has(placementId)) {
        delete bannerCache[placementId];
        prunedPlacementIds.add(placementId);
      }
    }
    if (prunedPlacementIds.size > 0) {
      for (const listener of pruneListeners.values()) {
        listener(prunedPlacementIds);
      }
    }

    if (queuedSettleCallbacks.length === 0) {
      return;
    }

    // React's dev-only StrictMode double-invoke unmounts and remounts a
    // fresh effect synchronously on first mount, before this request has
    // even settled - re-registering the exact same placement IDs and
    // queuing a settle callback for them. Those IDs are already covered by
    // the request that just completed, and its broadcast already reached
    // every subscriber still live at that point (including the remounted
    // one) - so firing another real request for the same data would only
    // risk the SDK's own per-endpoint rate limit declining it, leaving
    // these callers stuck until the retry above instead of ever seeing the
    // banner they already have. Settle them directly whenever the
    // newly-queued union adds nothing new, and only issue a fresh request
    // when it actually does.
    const pendingUnion = getRegisteredPlacementIdsUnion();
    const isFullyCovered = pendingUnion.every((placementId) => fetchedPlacementIds.has(placementId));
    if (isFullyCovered) {
      const callbacksToSettle = queuedSettleCallbacks;
      queuedSettleCallbacks = [];
      callbacksToSettle.forEach((settle) => settle());
      return;
    }

    dispatchCoordinatedRefresh();
  };

  const watchdogId = window.setTimeout(() => finishRound(false), SETTLE_TIMEOUT_MS);
  requestBannersRefresh(
    union,
    () => finishRound(true),
    () => finishRound(false),
  );
}

/**
 * Schedules `dispatchCoordinatedRefresh` on a microtask, unless a round is already in flight or
 * one is already scheduled (in which case that round already covers whatever's newly registered).
 *
 * Every `/banners/sync` response REPLACES the SDK's own internal banner store
 * with only the placement IDs *that specific request* asked for - not a merge
 * with whatever it already knew (see `braze-web-sdk`'s banner provider). A
 * component mounted well above the routed page content (e.g. the cart side
 * banner, registered from the site-wide header) reliably runs its effect -
 * and so calls this - before a newly-navigated-to page's own `useBrazeBanners`
 * effect does, on every navigation. Dispatching immediately would fire a real
 * request for that first caller's placement IDs alone, wiping the SDK's
 * memory of every other placement it previously knew (e.g. an account page's
 * top/side banners) until a corrective follow-up request completes - which
 * itself can be silently dropped by the SDK's own local rate limiting when it
 * follows so closely behind, leaving the corrected data to arrive only once
 * the `SETTLE_TIMEOUT_MS` watchdog above eventually retries.
 *
 * Deferring to a microtask instead means every `useBrazeBanners` instance
 * that registers during the same synchronous batch - which every instance
 * mounting or re-registering from the same navigation's render commit does -
 * is folded into the very first request for that batch, rather than racing
 * to be first and narrowing the SDK's store out from under everyone else.
 */
function scheduleDispatch() {
  if (inFlightUnion !== null || dispatchScheduled) {
    return;
  }
  dispatchScheduled = true;
  queueMicrotask(() => {
    dispatchScheduled = false;
    dispatchCoordinatedRefresh();
  });
}

/**
 * Queues `settle` for the next round and schedules one via `scheduleDispatch`.
 * @param settle - Called once the round covering the caller's placement IDs settles.
 */
function requestCoordinatedBannersRefresh(settle: SettleCallback) {
  queuedSettleCallbacks.push(settle);
  scheduleDispatch();
}

/**
 * Clears every cached banner and, if anything is still registered, immediately schedules a
 * fresh coordinated refresh for it. `braze.changeUser()` and `braze.wipeData()` swap or wipe the
 * SDK's own local banner storage out from under `bannerCache`/`knownPlacementIds` - without this,
 * a currently-mounted carousel or side banner would keep trusting stale cached data (or a stale
 * "already fetched" state) indefinitely, since nothing else ever tells the coordinator that
 * happened. Call this right after `braze.changeUser()` or `braze.wipeData()`.
 */
export function invalidateBrazeBannersCache() {
  bannerCache = {};
  knownPlacementIds = new Set();
  if (inFlightUnion !== null) {
    // A round is already in flight for the pre-invalidation state - `scheduleDispatch` would
    // no-op while it's running, and once it settles it would otherwise overwrite the reset above
    // with its own (now stale) results. Flag it so that round's own `finishRound` forces a fresh
    // dispatch for whatever's still registered once it settles, instead of just this round.
    invalidatedDuringInFlightRound = true;
    return;
  }
  if (getRegisteredPlacementIdsUnion().length > 0) {
    scheduleDispatch();
  }
}

/** Test-only: clears coordinator state so one test's unsettled request can't leak into the next. */
export function resetBrazeBannersCoordinatorForTests() {
  registrations = new Map();
  nextRegistrationId = 0;
  inFlightUnion = null;
  inFlightSettleCallbacks = [];
  queuedSettleCallbacks = [];
  dispatchScheduled = false;
  invalidatedDuringInFlightRound = false;
  bannerCache = {};
  knownPlacementIds = new Set();
  pruneListeners = new Map();
  window.clearTimeout(retryTimeoutId);
  retryTimeoutId = undefined;
  retryDelayMs = RETRY_BASE_DELAY_MS;
}

/**
 * Subscribes to Braze Banners updates for the given placement IDs and
 * returns only the placements that currently have a banner assigned,
 * in the same order as `placementIds`. Includes A/B holdout control
 * banners - the renderer is responsible for hiding those.
 * @param placementIds - Braze Banner placement IDs to subscribe to, in display order.
 * @returns The resolved banners, and whether the refresh request is still in flight.
 */
export function useBrazeBanners(placementIds: readonly string[]): UseBrazeBannersResult {
  const { publicEnv } = useRootLoaderData();
  const brazeApiKey = publicEnv.PUBLIC_BRAZE_API_KEY;
  const baseUrl = publicEnv.PUBLIC_BRAZE_BASE_URL;
  // The SDK's `requestBannersRefresh` can silently drop a call (no success or
  // error callback, ever - see below) if it lands too soon after another one
  // to the same endpoint. A caller that only fetches once for its whole
  // mounted lifetime (e.g. the account layout fetching all of its sub-pages'
  // placements just once, or the cart drawer's banner staying mounted across
  // every route) has no way to recover if that one attempt is the one that
  // gets dropped - the banner then stays missing for the rest of the visit.
  // Re-running the fetch on every page navigation, not just on mount, gives
  // every page load its own chance to succeed.
  const { pathname } = useLocation();
  const [bannersByPlacementId, setBannersByPlacementId] = useState(() => getCachedBanners(placementIds));
  // Skip the loading state entirely when every placement is already cached (e.g. a
  // remount of a placement another mount - or this same component, previously -
  // already resolved), so a fresh mount doesn't flash a skeleton over content it
  // already knows, and stays visible even if the SDK drops its background refresh.
  const [isLoading, setIsLoading] = useState(
    () => placementIds.length > 0 && placementIds.some((placementId) => !knownPlacementIds.has(placementId)),
  );
  const registrationIdRef = useRef<number | null>(null);
  if (registrationIdRef.current === null) {
    registrationIdRef.current = nextRegistrationId++;
  }

  useEffect(() => {
    if (!brazeApiKey || !baseUrl || placementIds.length === 0) {
      setIsLoading(false);
      return;
    }

    // Every placement is already known (from this or another mount) - skip the
    // loading state so already-visible content doesn't flash to a skeleton while
    // a background refresh (which may itself be dropped, see SETTLE_TIMEOUT_MS)
    // runs.
    if (placementIds.some((placementId) => !knownPlacementIds.has(placementId))) {
      setIsLoading(true);
    }
    brazeInit(brazeApiKey, baseUrl);

    // A broadcast only carries keys for the placement IDs its own request
    // asked for - it's never a full snapshot of everyone's placements. When
    // it's the result of a different, now-superseded request (a coordinator
    // round that ran before this instance registered, or the SDK's own
    // internal retry firing late with a stale, narrower placement list),
    // our own IDs are simply absent from it. Only applying keys that are
    // actually present keeps our last-known banner instead of blanking it
    // out because of a sync response that was never about us.
    const readBanners = (update: Record<string, BrazeBanner | null>) => {
      for (const placementId of Object.keys(update)) {
        bannerCache[placementId] = update[placementId];
      }

      setBannersByPlacementId((previous) => {
        let changed = false;
        const next = { ...previous };
        for (const placementId of placementIds) {
          if (placementId in update && next[placementId] !== update[placementId]) {
            next[placementId] = update[placementId];
            changed = true;
          }
        }
        return changed ? next : previous;
      });
    };

    const subscriptionId = subscribeToBannersUpdates(readBanners);

    let settled = false;
    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      setIsLoading(false);
    };

    const registrationId = registrationIdRef.current as number;
    registrations.set(registrationId, placementIds);
    // A round dispatched before this instance's own placement IDs made it into the union can
    // still prune one of them from `bannerCache` (see the module comment above `dispatchCoordinatedRefresh`'s
    // prune step) - without this, this instance would keep rendering that stale banner from its own
    // local state until a follow-up round happens to cover it.
    pruneListeners.set(registrationId, (prunedPlacementIds) => {
      setBannersByPlacementId((previous) => {
        let changed = false;
        const next = { ...previous };
        for (const placementId of prunedPlacementIds) {
          if (placementId in next) {
            delete next[placementId];
            changed = true;
          }
        }
        return changed ? next : previous;
      });
    });
    requestCoordinatedBannersRefresh(settle);
    const settleTimeoutId = window.setTimeout(settle, SETTLE_TIMEOUT_MS);

    return () => {
      settled = true;
      window.clearTimeout(settleTimeoutId);
      registrations.delete(registrationId);
      pruneListeners.delete(registrationId);
      removeBrazeSubscription(subscriptionId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brazeApiKey, baseUrl, placementIds, pathname]);

  const banners = useMemo(
    () =>
      placementIds
        .map((placementId) => bannersByPlacementId[placementId])
        .filter((banner): banner is BrazeBanner => !!banner),
    [placementIds, bannersByPlacementId],
  );

  return { banners, isLoading };
}
